import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ProjectService } from '../../services/project.service';
import { EmployeeService } from '../../services/employee.service';
import { ClientService } from '../../services/client.service';
import { RoleService } from '../../services/role.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { AuthService, UserInfo } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { Project } from '../../models/project.model';
import { Employee } from '../../models/employee.model';
import { Client } from '../../models/client.model';
import { Role } from '../../models/role.model';
import { ServiceGroup } from '../../models/service-group.model';
import { ProjectDetailsComponent } from '../project-details/project-details.component';

@Component({
  selector: 'app-projects-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Select,
    Tooltip,
    ProjectDetailsComponent
  ],
  templateUrl: './projects-list.component.html',
  styleUrl: './projects-list.component.scss'
})
export class ProjectsListComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  projects: Project[] = [];
  filteredProjects: Project[] = [];
  employees: Employee[] = [];
  clients: Client[] = [];
  roles: Role[] = [];
  serviceGroups: ServiceGroup[] = [];
  user: UserInfo | null = null;
  currentUserEmployee: Employee | null = null;
  isAccountManager: boolean = false;

  loading: boolean = true;
  displayDetailsDialog: boolean = false;
  selectedProject: Project | null = null;
  searchText: string = '';

  // View mode: 'list', 'grid' or the progress Kanban board
  viewMode: 'grid' | 'list' | 'kanban' = 'list';

  /**
   * Kanban columns, one per delivery progress state. Delivery projects have no
   * sales stage, so the board groups by Progress — the closest thing to a
   * workflow, reading left (not started) to right (finished).
   */
  kanbanColumns: { key: string; label: string; cssClass: string; projects: Project[] }[] = [];

  /**
   * Columns rendered on the board. Every progress state is always shown — the
   * gaps are informative, and a board with a stable shape is easier to read.
   */
  get visibleKanbanColumns() {
    return this.kanbanColumns;
  }

  // Filter properties
  // Default is 'rag' — hide projects that haven't been assigned a Red/Amber/Green health yet.
  // Users can still surface them via the explicit 'All' or 'Unrated' options.
  selectedStatusFilter: string | null = 'rag';
  selectedProgressFilter: string | null = null;
  selectedAccountManagerFilter: string | null = null;
  selectedManagerFilter: string | null = null;
  selectedServiceGroupFilter: string | null = null;
  selectedClientFilter: string | null = null;

  // Filter options
  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'RAG Only', value: 'rag' },
    { label: 'All', value: null },
    { label: 'Red', value: 'Red' },
    { label: 'Amber', value: 'Amber' },
    { label: 'Green', value: 'Green' },
    { label: 'Unrated', value: 'unrated' }
  ];

  progressFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'To start', value: 'To start' },
    { label: 'On track', value: 'On track' },
    { label: 'Off track', value: 'Off track' },
    { label: 'Blocker', value: 'Blocker' },
    { label: 'On Hold', value: 'On Hold' },
    { label: 'Closed', value: 'Closed' }
  ];

  accountManagerFilterOptions: { label: string; value: string | null }[] = [];
  managerFilterOptions: { label: string; value: string | null }[] = [];
  serviceGroupFilterOptions: { label: string; value: string | null }[] = [];
  clientFilterOptions: { label: string; value: string | null }[] = [];

  rows = 20;
  first = 0;
  totalRecords = 0;
  rowsPerPageOptions = [10, 20, 50, 100];

  get totalProjectsCount(): number { return this.projects.length; }
  get filteredCount(): number { return this.filteredProjects.length; }

  constructor(
    private projectService: ProjectService,
    private employeeService: EmployeeService,
    private clientService: ClientService,
    private roleService: RoleService,
    private serviceGroupService: ServiceGroupService,
    private authService: AuthService,
    private messageService: MessageService,
    public pageHeaderService: PageHeaderSearchActionService
  ) { }

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by project name, client, PM, or Account Manager...');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.pageHeaderService.setCanShowPrimaryAction(false);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.user = user;
      if (user) {
        this.loadData();
      }
    });
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applyRoleBasedFilter();
    });
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.exportToExcel());
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  loadData() {
    this.loading = true;
    
    // Try to load roles, but don't fail if it's not accessible (non-admin users)
    const rolesObservable = this.roleService.getAllRoles().pipe(
      catchError(error => {
        // Non-admin users can't access roles, that's okay
        return of([] as Role[]);
      })
    );
    
    forkJoin({
      projects: this.projectService.getAllProjects().pipe(
        catchError(error => {
          // Don't log 403 errors - expected for authorization failures
          if (error.status !== 403) {
            console.error('Error loading projects:', error);
          }
          return of([] as Project[]);
        })
      ),
      employees: this.employeeService.getAllEmployees().pipe(
        catchError(error => {
          // Don't log 403 errors - expected for non-admin users (PM/Finance)
          if (error.status !== 403) {
            console.error('Error loading employees:', error);
          }
          return of([] as Employee[]);
        })
      ),
      clients: this.clientService.getAllClients().pipe(
        catchError(error => {
          // Don't log 403 errors - expected for non-admin users (PM/Finance)
          if (error.status !== 403) {
            console.error('Error loading clients:', error);
          }
          return of([] as Client[]);
        })
      ),
      roles: rolesObservable,
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(
        catchError(error => {
          if (error.status !== 403) {
            console.error('Error loading service groups:', error);
          }
          return of([] as ServiceGroup[]);
        })
      )
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.projects = (data.projects || []).filter(p => p.isActive);
        this.employees = data.employees || [];
        this.clients = (data.clients || []).filter(c => c.isActive);
        this.roles = data.roles || [];
        this.serviceGroups = (data.serviceGroups || []).filter(sg => sg.isActive);
        
        // Build filter options
        this.buildFilterOptions();
        
        // Find current user's employee record by email
        if (this.user?.email) {
          this.currentUserEmployee = this.employees.find(
            e => e.email?.toLowerCase() === this.user!.email.toLowerCase()
          ) || null;
          
          // Check if user is an Account Manager (roleIds or primary roleId)
          if (this.currentUserEmployee) {
            const accountManagerRole = this.roles.find(
              r => r.title?.toLowerCase().includes('account manager')
            );
            if (accountManagerRole) {
              this.isAccountManager =
                (this.currentUserEmployee.roleIds && this.currentUserEmployee.roleIds.includes(accountManagerRole.id)) ||
                this.currentUserEmployee.roleId === accountManagerRole.id;
            }
          }
        }
        
        // Apply role-based filtering
        this.applyRoleBasedFilter();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading data:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load projects. Please refresh the page or try again later.'
        });
        this.loading = false;
      }
    });
  }

  buildFilterOptions() {
    // Build Account Manager filter options (employees with Account Manager in roleIds or roleId)
    const accountManagerRole = this.roles.find(r =>
      r.title?.toLowerCase().includes('account manager')
    );
    const accountManagers = accountManagerRole
      ? this.employees.filter(e => e.isActive && (
        (e.roleIds && e.roleIds.includes(accountManagerRole.id)) || e.roleId === accountManagerRole.id
      ))
      : [];
    
    this.accountManagerFilterOptions = [
      { label: 'All', value: null },
      ...accountManagers.map(am => ({
        label: am.name || am.email || 'Unknown',
        value: am.id
      }))
    ];

    // Build Project Manager filter options.
    // Match the role title exactly ('pm' as a substring also matches "Develop-pm-ent", which put
    // Learning & Development staff in this list), and collect EVERY matching role rather than the
    // first - roles come back unordered, so find() could latch onto the wrong one.
    const pmRoleIds = this.roles
      .filter(r => {
        const title = r.title?.toLowerCase().trim();
        return title === 'project manager' || title === 'pm';
      })
      .map(r => r.id);

    const projectManagers = pmRoleIds.length
      ? this.employees.filter(e => e.isActive && (
        (e.roleIds && e.roleIds.some(id => pmRoleIds.includes(id))) ||
        (e.roleId && pmRoleIds.includes(e.roleId))
      ))
      : [];

    this.managerFilterOptions = [
      { label: 'All', value: null },
      ...projectManagers
        .map(pm => ({
          label: pm.name || pm.email || 'Unknown',
          value: pm.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];

    // Build Service Group filter options
    const serviceGroupMap = new Map<string, string>();
    
    // Add service groups from projects
    this.projects.forEach(p => {
      if (p.serviceGroupId && p.serviceGroupName) {
        serviceGroupMap.set(p.serviceGroupId, p.serviceGroupName);
      }
    });
    
    // Add all service groups from database
    this.serviceGroups.forEach(sg => {
      if (sg.id && sg.name) {
        serviceGroupMap.set(sg.id, sg.name);
      }
    });

    this.serviceGroupFilterOptions = [
      { label: 'All', value: null },
      ...Array.from(serviceGroupMap.entries())
        .map(([id, name]) => ({ label: name, value: id }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];

    // Build Client filter options
    this.clientFilterOptions = [
      { label: 'All', value: null },
      ...this.clients
        .filter(c => c.isActive)
        .map(c => ({
          label: c.name || 'Unknown',
          value: c.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ];
  }

  applyRoleBasedFilter() {
    if (!this.user) {
      this.filteredProjects = [];
      return;
    }

    let filtered = [...this.projects];

    // Apply role-based filtering
    // Admin, Finance, and Account Managers see all projects
    // PMs see only projects managed by them
    if (this.user.isAdmin || this.user.isFinance || this.isAccountManager) {
      // No additional filtering
    } else if (this.user.isPM) {
      if (this.currentUserEmployee) {
        const pmEmployeeId = this.currentUserEmployee.id;
        filtered = filtered.filter(p => p.managerId === pmEmployeeId);
      } else {
        // If we cannot resolve the employee record, show nothing to PM
        filtered = [];
      }
    } else {
      // Other roles see nothing here
      filtered = [];
    }

    // Apply search filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(proj =>
        proj.name?.toLowerCase().includes(searchLower) ||
        proj.clientName?.toLowerCase().includes(searchLower) ||
        proj.managerName?.toLowerCase().includes(searchLower) ||
        proj.accountManagerName?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter.
    //   'rag'      → show only Red / Amber (incl. legacy Yellow) / Green — default view
    //   'unrated'  → show only projects without a RAG status set
    //   null       → show everything
    //   'Red' | 'Amber' | 'Green' → exact match (Amber also matches legacy 'Yellow')
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'rag') {
        filtered = filtered.filter(p => this.hasRagStatus(p.status));
      } else if (this.selectedStatusFilter === 'unrated') {
        filtered = filtered.filter(p => !this.hasRagStatus(p.status));
      } else {
        filtered = filtered.filter(p =>
          p.status === this.selectedStatusFilter ||
          (this.selectedStatusFilter === 'Amber' && p.status === 'Yellow')
        );
      }
    }

    // Apply progress filter
    if (this.selectedProgressFilter !== null) {
      filtered = filtered.filter(p => p.progress === this.selectedProgressFilter);
    }

    // Apply account manager filter
    if (this.selectedAccountManagerFilter !== null) {
      filtered = filtered.filter(p => p.accountManagerId === this.selectedAccountManagerFilter);
    }

    // Apply manager filter
    if (this.selectedManagerFilter !== null) {
      filtered = filtered.filter(p => p.managerId === this.selectedManagerFilter);
    }

    // Apply service group filter
    if (this.selectedServiceGroupFilter !== null) {
      filtered = filtered.filter(p => p.serviceGroupId === this.selectedServiceGroupFilter);
    }

    // Apply client filter
    if (this.selectedClientFilter !== null) {
      filtered = filtered.filter(p => p.clientId === this.selectedClientFilter);
    }

    // Grid view: prioritize attention-needing projects first (Red → Amber → Green → others).
    // List view keeps the server/default order so sortable columns remain predictable.
    // Card views lead with the projects most at risk.
    if (this.viewMode === 'grid' || this.viewMode === 'kanban') {
      filtered = [...filtered].sort(
        (a, b) => this.getStatusSortWeight(a.status) - this.getStatusSortWeight(b.status)
      );
    }

    this.filteredProjects = filtered;
    this.totalRecords = this.filteredProjects.length;
    if (this.viewMode === 'kanban') this.rebuildKanbanColumns();
    this.pageHeaderService.setExportDisabled(this.filteredProjects.length === 0);
  }

  /** Red first, then Amber/Yellow, then Green, then anything else — for the grid view only. */
  private getStatusSortWeight(status?: string): number {
    const s = (status || '').toLowerCase();
    if (s === 'red') return 0;
    if (s === 'amber' || s === 'yellow') return 1;
    if (s === 'green') return 2;
    return 3;
  }

  /** True if a project has an explicit Red / Amber (incl. legacy Yellow) / Green health status. */
  private hasRagStatus(status?: string): boolean {
    const s = (status || '').toLowerCase();
    return s === 'red' || s === 'amber' || s === 'yellow' || s === 'green';
  }

  onStatusFilterChange() {
    this.applyRoleBasedFilter();
  }

  onProgressFilterChange() {
    this.applyRoleBasedFilter();
  }

  onAccountManagerFilterChange() {
    this.applyRoleBasedFilter();
  }

  onManagerFilterChange() {
    this.applyRoleBasedFilter();
  }

  onServiceGroupFilterChange() {
    this.applyRoleBasedFilter();
  }

  onClientFilterChange() {
    this.applyRoleBasedFilter();
  }

  hasActiveFilters(): boolean {
    // 'rag' is the default status view — don't treat it as an "active" filter.
    return !!(
      (this.selectedStatusFilter && this.selectedStatusFilter !== 'rag') ||
      this.selectedProgressFilter ||
      this.selectedAccountManagerFilter ||
      this.selectedManagerFilter ||
      this.selectedServiceGroupFilter ||
      this.selectedClientFilter
    );
  }

  clearFilters() {
    this.searchText = '';
    this.selectedStatusFilter = 'rag'; // reset to default (hide unrated)
    this.selectedProgressFilter = null;
    this.selectedAccountManagerFilter = null;
    this.selectedManagerFilter = null;
    this.selectedServiceGroupFilter = null;
    this.selectedClientFilter = null;
    this.applyRoleBasedFilter();
  }

  onSearch() {
    this.applyRoleBasedFilter();
  }

  clearSearch() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.applyRoleBasedFilter();
  }

  showProjectDetails(project: Project, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedProject = project;
    this.displayDetailsDialog = true;
  }

  hideDetailsDialog() {
    this.displayDetailsDialog = false;
    this.selectedProject = null;
  }

  onProjectUpdated(updatedProject: Project) {
    // Update the project in the list
    const index = this.projects.findIndex(p => p.id === updatedProject.id);
    if (index !== -1) {
      this.projects[index] = updatedProject;
      // Reapply filters
      this.applyRoleBasedFilter();
    }
  }

  getStatusSeverity(status?: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (!status) return 'secondary';
    const statusLower = status.toLowerCase();
    if (statusLower === 'green') return 'success';
    if (statusLower === 'amber' || statusLower === 'yellow') return 'warn';
    if (statusLower === 'red') return 'danger';
    return 'secondary';
  }

  getStatusIconClass(status?: string): string {
    if (!status) return 'status-default';
    const statusLower = status.toLowerCase();
    if (statusLower === 'red') return 'status-red';
    if (statusLower === 'amber' || statusLower === 'yellow') return 'status-amber';
    if (statusLower === 'green') return 'status-green';
    return 'status-default';
  }

  getProgressSeverity(progress?: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (!progress) return 'secondary';
    const progressLower = progress.toLowerCase();
    // To start - Gray (secondary)
    if (progressLower.includes('to start')) return 'secondary';
    // On track - Green (success)
    if (progressLower.includes('on track')) return 'success';
    // Closed - Green (success)
    if (progressLower.includes('closed')) return 'success';
    // Off track - Amber (warn)
    if (progressLower.includes('off track')) return 'warn';
    // Blocker - Red (danger)
    if (progressLower.includes('blocker')) return 'danger';
    // On Hold - Red (danger)
    if (progressLower.includes('on hold')) return 'danger';
    return 'secondary';
  }

  getStatusBadgeClass(status?: string): string {
    if (!status) return 'status-badge-default';
    const statusLower = status.toLowerCase();
    if (statusLower === 'green') return 'status-badge-green';
    if (statusLower === 'amber' || statusLower === 'yellow') return 'status-badge-amber';
    if (statusLower === 'red') return 'status-badge-red';
    return 'status-badge-default';
  }

  getProgressBadgeClass(progress?: string): string {
    if (!progress) return 'progress-badge-default';
    const progressLower = progress.toLowerCase();
    // To start - Gray
    if (progressLower.includes('to start')) return 'progress-badge-secondary';
    // On track - Green
    if (progressLower.includes('on track')) return 'progress-badge-success';
    // Closed - Green
    if (progressLower.includes('closed')) return 'progress-badge-success';
    // Off track - Amber
    if (progressLower.includes('off track')) return 'progress-badge-warn';
    // Blocker - Red
    if (progressLower.includes('blocker')) return 'progress-badge-danger';
    // On Hold - Red
    if (progressLower.includes('on hold')) return 'progress-badge-danger';
    return 'progress-badge-default';
  }

  // ---- Kanban card helpers (shared shape with the presales boards) ----
  //
  // The delivery card carries effort where a presales card carries deal value:
  // revenue and winning % are presales-only fields, but planned vs actual days
  // answers the same question here — is this one on track against its budget.

  /** Up to two initials for a card avatar; '?' when the name is missing. */
  initials(name?: string | null): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }

  /** Deterministic avatar tint from a name, so a person keeps one colour. */
  avatarHue(name?: string | null): number {
    const s = name || '';
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
    return [210, 150, 275, 25, 340, 190][h % 6];
  }

  /**
   * Effort consumed as a percentage of the planned estimate. Null when no
   * estimate is set, so the card omits the bar rather than showing a bar that
   * silently means "unknown". Not capped at 100 — going over budget is exactly
   * what this is meant to surface; the bar clamps visually, the number does not.
   */
  effortPct(project: Project): number | null {
    const planned = project.plannedEstimateDays;
    if (planned == null || planned <= 0) return null;
    const actual = project.actualEffortDays ?? 0;
    return Math.round((actual / planned) * 100);
  }

  /** Bar width, clamped to the track; the label still reports the true figure. */
  effortBarPct(project: Project): number {
    return Math.min(100, Math.max(0, this.effortPct(project) ?? 0));
  }

  /** Whole days past the end date, or null when unset / not yet due. */
  calcOverdueDays(endDate?: string | null): number | null {
    if (!endDate) return null;
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return null;
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.floor((today.getTime() - end.getTime()) / 86400000);
    return days > 0 ? days : null;
  }

  /** Days until the end date — negative once past, null when unset. */
  private daysToEnd(iso?: string | null): number | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - today.getTime()) / 86400000);
  }

  /** "Ends in 5d" / "Ends today", falling back to the date when far out. */
  endLabel(iso?: string | null): string {
    const d = this.daysToEnd(iso);
    if (d == null) return '';
    if (d === 0) return 'Ends today';
    if (d > 0)   return d > 60 ? this.shortDate(iso) : `Ends in ${d}d`;
    return this.shortDate(iso);
  }

  /** True when the end date is inside the next 14 days. */
  isEndingSoon(iso?: string | null): boolean {
    const d = this.daysToEnd(iso);
    return d != null && d >= 0 && d <= 14;
  }

  /**
   * Compact date for a card. formatDate() includes the weekday, which is too
   * long for a card foot sharing its line with the owner.
   */
  shortDate(iso?: string | null): string {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  setViewMode(mode: 'grid' | 'list' | 'kanban') {
    this.viewMode = mode;
    this.applyRoleBasedFilter();
  }

  /**
   * Buckets the filtered projects into one column per progress state. Anything
   * with no progress set (or an unrecognised legacy value) lands in "Not set"
   * rather than vanishing from the board.
   */
  private rebuildKanbanColumns(): void {
    const states = this.progressFilterOptions
      .filter(o => o.value !== null)
      .map(o => ({ key: o.value as string, label: o.label }));

    const columns = states.map(s => ({
      key: s.key,
      label: s.label,
      cssClass: 'progress-' + s.key.toLowerCase().replace(/\s+/g, '-'),
      projects: [] as Project[],
    }));
    const unset = { key: '', label: 'Not set', cssClass: 'progress-unset', projects: [] as Project[] };

    for (const p of this.filteredProjects) {
      const col = columns.find(c => c.key.toLowerCase() === (p.progress ?? '').toLowerCase());
      (col ?? unset).projects.push(p);
    }
    // Only offer the catch-all column when something actually needs it.
    this.kanbanColumns = unset.projects.length > 0 ? [...columns, unset] : columns;
  }

  trackByProjectId(_index: number, project: Project): string {
    return project.id || '';
  }

  exportToExcel() {
    if (!this.filteredProjects || this.filteredProjects.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No projects to export'
      });
      return;
    }

    try {
      // Prepare data for Excel export
      const exportData = this.filteredProjects.map((project, index) => ({
        '#': index + 1,
        'Project Code': project.projectCode || '-',
        'Project Name': project.name || '-',
        'Client': project.clientName || '-',
        'Account Manager': project.accountManagerName || '-',
        'PM': project.managerName || '-',
        'Service Group': project.serviceGroupName || '-',
        'Status': project.status || '-',
        'Progress': project.progress || '-',
        'Start Date': project.startDate ? this.formatDateForExcel(project.startDate) : '-',
        'End Date': project.endDate ? this.formatDateForExcel(project.endDate) : '-'
      }));

      // Create worksheet
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [
        { wch: 5 },   // #
        { wch: 15 },  // Project Code
        { wch: 30 },  // Project Name
        { wch: 20 },  // Client
        { wch: 20 },  // Account Manager
        { wch: 20 },  // PM
        { wch: 20 },  // Service Group
        { wch: 12 },  // Status
        { wch: 15 },  // Progress
        { wch: 15 },  // Start Date
        { wch: 15 }   // End Date
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Projects': worksheet },
        SheetNames: ['Projects']
      };

      // Generate Excel file
      const excelBuffer: any = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      // Save file
      const data: Blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const fileName = `Active_Projects_${this.formatDateForExcel(new Date().toISOString())}.xlsx`;
      saveAs(data, fileName);

      this.messageService.add({
        severity: 'success',
        summary: 'Export Successful',
        detail: 'Projects exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Export Failed',
        detail: 'Failed to export projects to Excel'
      });
    }
  }

  formatDateForExcel(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }
}

